import { PersonAdd } from '@fs/zion-icon'
import { Alert, LayoutBand, Button, Cell, colors, FlowGrid, H5, List, Row, Separator, Subheading } from '@fs/zion-ui'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import PersonDisplay from './PersonDisplay'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { formatRecordDisplayData, getPrimaryPersonName } from '../utils/record.utils'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { sortPersonsByPrimary } from '../utils/display.utils'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'

/**
 * HouseholdDisplay
 *
 * Displays a household/record with its associated persons. Shows a card header with record details
 * and "Add Person" buttons (both in header and footer) to add new persons to this household.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.household - The household object to display
 * @param {Array} props.persons - Array of person objects belonging to this household from the useHouseholdList hook
 * @param {Function} props.setSelectedRecordId - Callback to set the selected record ID when adding a person
 * @param {Function} props.onPersonSelect - Callback when a person in the list is selected
 * @returns {JSX.Element}
 */
const HouseholdDisplay = ({ household, persons, setSelectedRecordId, onPersonSelect }) => {
  const [t] = useTranslation()
  const { openOverlay } = useOverlayContext()
  const { highlightedPersonId } = useR2StateContext()
  const { isReadOnlyCollection } = useCollectionContextProvider()

  const handleAddPersonClick = useCallback(
    (householdId) => {
      setSelectedRecordId(householdId)
      openOverlay('addNamesOverlay')
    },
    [openOverlay, setSelectedRecordId]
  )

  const primaryPersonName = getPrimaryPersonName(household)
  const { primaryEvent, primaryEventSummary } = formatRecordDisplayData(household)
  const sortedPersons = sortPersonsByPrimary(persons)

  return (
    <>
      <LayoutBand color={colors.gray02} marginY="xs">
        <Row>
          <Cell growX alignY="middle">
            <H5>
              {primaryPersonName
                ? t('household-display.household-with-name', '{primaryPersonName} Household', { primaryPersonName })
                : t('household-display.household', 'Household')}
              {primaryEventSummary && <Subheading>{primaryEventSummary}</Subheading>}
            </H5>
          </Cell>
        </Row>
      </LayoutBand>
      <Separator size="nano" />
      <List>
        {sortedPersons.map((person) => (
          <PersonDisplay
            key={person.id}
            person={person}
            record={household}
            onPersonSelect={onPersonSelect}
            isSelected={highlightedPersonId === person?.id}
          />
        ))}
      </List>
      <Separator size="nano" />
      <FlowGrid columnCount={1} orientation="vertical" gutters="xs">
        {!primaryPersonName && primaryEvent && (
          <Alert type="warning" outlined>
            {t(
              'household-display.missing-primary-person',
              "A primary person is not assigned to this household. To assign one, edit a household member's essential information."
            )}
          </Alert>
        )}
        {primaryPersonName && !primaryEvent && (
          <Alert type="warning" outlined>
            {t(
              'household-display.missing-primary-event',
              "A primary event is not assigned to this household. To assign one, edit a household member's events."
            )}
          </Alert>
        )}
        {!primaryPersonName && !primaryEvent && (
          <Alert type="warning" outlined>
            {t(
              'household-display.missing-primary-person-and-event',
              "A primary person and event are not assigned to this household. To assign them, edit a household member's essential information and events."
            )}
          </Alert>
        )}
        <Row>
          <Button
            emphasis="medium"
            color="blue"
            Icon={PersonAdd}
            onClick={() => handleAddPersonClick(household.id)}
            linkName="Add to Household"
            data-testid="add-person-footer-button"
            disabled={isReadOnlyCollection}
          >
            {t('household-display.add-person-footer-button', 'Add Person')}
          </Button>
        </Row>
      </FlowGrid>
    </>
  )
}

export default HouseholdDisplay
